import { formatInTimeZone } from 'date-fns-tz';
import { useEffect, useMemo, useState, useRef, useCallback, FunctionComponent } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import MenuIcon from '../../../assets/images/icons/menu.svg';

import logoutIcon from '../../../assets/images/icons/logout.svg';
import messageCenterIcon from '../../../assets/images/icons/messagecenter.svg';
import getCareIcon from '../../../assets/images/icons/get-care.svg';
import homeIcon from '../../../assets/images/icons/home.png';
import resourceCenterIcon from '../../../assets/images/icons/resource-center.png';
import pastVisitsIcon from '../../../assets/images/icons/pastvisits.svg';
import settingsIcon from '../../../assets/images/icons/settings.svg';
import leftRightArrowsImg from '../../../assets/left-right-arrows-circle.svg';
import logoImg from '../../../assets/logo-dark-md.svg';
import logoMark from '../../../assets/logo-short.svg';
import AvatarPlaceholder from '../../../components/common/AvatarPlaceholder';
import CustomModal from '../../../components/modal/CustomModal';
import { useAppDispatch, useAppSelector } from '../../../store';
import { usernameFromFHIR4HumanName } from '../../../utils/general';
import {
  selectAccountState,
  selectPatientTimezone,
  toggleMenuCollapsed,
} from '../../account/api/accountSlice';
import { logoutUserThunk } from '../../auth/api/authSlice';
import { APP_LAYOUT_ROUTES, DEFAULT_APP_LAYOUT_ROUTE } from '../routes';

import './Sidebar.scss';
import { useTranslation } from 'react-i18next';
import { clearGetCareState } from '../../get-care/api/getCareSlice';
import { env } from '../../../env';

const sidebarItems = [
  { icon: homeIcon, ...APP_LAYOUT_ROUTES.HOME },
  { icon: pastVisitsIcon, ...APP_LAYOUT_ROUTES.PAST_VISITS },
  { icon: messageCenterIcon, ...APP_LAYOUT_ROUTES.MESSAGES },
  { icon: resourceCenterIcon, ...APP_LAYOUT_ROUTES.RESOURCE_CENTER },
  { icon: settingsIcon, ...APP_LAYOUT_ROUTES.SETTINGS },
  { icon: getCareIcon, ...APP_LAYOUT_ROUTES.GET_CARE },
];

const SidebarItem = (props: {
  title: string;
  path: string;
  icon: string;
  isMenuCollapsed: boolean;
  closeSidebar: () => void;
}) => {
  const { pathname } = useLocation();
  const isActive = pathname.includes(`/app/${props.path}`);
  const itemRef = useRef(null);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const handleClick = () => {
    props.closeSidebar();
    dispatch(clearGetCareState());
  };

  const SidebarLink = ({ title, path, icon }: { title: string; path: string; icon: string }) => {
    const linkContent = (
      <>
        <img src={icon} alt={`${title} icon`} className='app__sidebar-item-icon' />
        {!props.isMenuCollapsed && t(title)}
      </>
    );

    const linkProps = {
      to: path,
      onClick: handleClick,
    };

    if (title === 'Resource Center') {
      return (
        <a
          href={path}
          target='_blank'
          rel='noopener noreferrer'
          className='app__sidebar-item-link'
          onClick={handleClick}
        >
          {linkContent}
        </a>
      );
    }

    const classNames =
      title === 'Get Care'
        ? 'mt-4 btn btn-primary text-white text-start w-100'
        : 'app__sidebar-item-link';

    const styles =
      title === 'Get Care' && props.isMenuCollapsed
        ? { height: '45.13px', padding: '8px 12px' }
        : props.isMenuCollapsed
          ? { height: '26px' }
          : {};

    return (
      <Link {...linkProps} className={classNames} style={styles}>
        {linkContent}
      </Link>
    );
  };

  return (
    <div
      className={`app__sidebar-item ${
        isActive && props.title !== 'Get Care' ? 'app__sidebar-item--active' : ''
      } ${props.title === 'Get Care' ? 'mb-3 px-0' : ''}`}
      ref={itemRef}
      title={props.title}
    >
      {props.isMenuCollapsed && props.title !== 'Get Care' ? (
        <OverlayTrigger
          container={itemRef}
          rootClose={false}
          trigger={['hover', 'focus']}
          placement='right'
          overlay={<Tooltip className='text-gray-700'>{props.title}</Tooltip>}
        >
          <SidebarLink title={props.title} path={props.path} icon={props.icon} />
        </OverlayTrigger>
      ) : (
        <SidebarLink title={props.title} path={props.path} icon={props.icon} />
      )}
    </div>
  );
};

const SidebarMenu = (props: { isMenuCollapsed: boolean; closeSidebar: () => void }) => {
  return (
    <div>
      {sidebarItems.map((item) => {
        return (
          <SidebarItem
            {...item}
            key={item.title}
            isMenuCollapsed={props.isMenuCollapsed}
            closeSidebar={props.closeSidebar}
          />
        );
      })}
    </div>
  );
};

const SidebarCard = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const { t }: any = useTranslation();
  const timezone = useAppSelector(selectPatientTimezone);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDate(new Date());
    }, 60 * 1000);
    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <div className='app__sidebar-card'>
      <div>
        <div className='app__sidebar-card-time-date'>
          {formatInTimeZone(currentDate, timezone, 'hh:mm a')} |{' '}
          {formatInTimeZone(currentDate, timezone, 'MMM dd, yyyy')}
        </div>
        <div className='app__sidebar-card-time-zone'>
          {t('Local Time')} ({formatInTimeZone(currentDate, timezone, 'O')})
        </div>
      </div>
      {env.REACT_APP_VERSION !== '' && (
        <div className='w-100 d-flex flex-row justify-content-between align-items-center flex-wrap'>
          <p className='app__sidebar-card-version m-0'>{env.REACT_APP_VERSION}</p>
          <p className='app__sidebar-card-copyright m-0'>&copy; All rights reserved.</p>
        </div>
      )}
    </div>
  );
};

const SidebarProfile = (props: { setShowLogoutModal: (show: boolean) => void }) => {
  const { patient, avatar, sidebarMenuCollapsed } = useAppSelector(selectAccountState);
  const { t }: any = useTranslation();
  const handleShowLogout = () => {
    props.setShowLogoutModal(true);
  };

  const practitionerName = useMemo(
    () => (patient?.name?.[0] ? usernameFromFHIR4HumanName(patient.name[0]) : ''),
    [patient?.name],
  );

  return (
    <div
      className={`app__sidebar-profile ${
        sidebarMenuCollapsed ? 'app__sidebar-profile-collapsed' : ''
      }`}
    >
      <div className='app__sidebar-profile-user-wrapper'>
        <button
          className='border-0 bg-transparent p-0'
          onClick={sidebarMenuCollapsed ? handleShowLogout : undefined}
        >
          {!avatar ? (
            <AvatarPlaceholder
              style={{ width: 40, height: 40 }}
              stringToUse={patient?.name?.[0]?.given?.[0] || patient?.name?.[0]?.family}
              className={`app__sidebar-profile-avatar ${
                sidebarMenuCollapsed ? 'app__sidebar-profile-avatar-collapsed' : ''
              } `}
            />
          ) : (
            <img
              src={avatar?.url}
              className={`app__sidebar-profile-avatar ${
                sidebarMenuCollapsed ? 'app__sidebar-profile-avatar-collapsed' : ''
              }`}
              alt='avatar'
            />
          )}
        </button>
        <div>
          {!sidebarMenuCollapsed && (
            <>
              <p className='app__sidebar-profile-name'>{practitionerName}</p>
              <p className='app__sidebar-patient-code'>
                {t('Patient ID')} : <b>{patient?.patientCode ?? '-'}</b>
              </p>
            </>
          )}
        </div>
      </div>

      {!sidebarMenuCollapsed && (
        <button className='border-0 bg-transparent p-0' onClick={handleShowLogout}>
          <img src={logoutIcon} alt='logout btn' />
        </button>
      )}
    </div>
  );
};

const Sidebar: FunctionComponent = () => {
  const { sidebarMenuCollapsed } = useAppSelector(selectAccountState);
  const MOBILE_BREAKPOINT = 768;
  const [showConfirmMessageDialog, setShowConfirmMessageDialog] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [showSidebar, setShowSidebar] = useState(true);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();

  const handleSidebarChange = useCallback(() => {
    setShowSidebar((prev) => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setShowSidebar(false);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node) &&
        headerRef.current &&
        !headerRef.current.contains(event.target as Node) &&
        showSidebar
      ) {
        closeSidebar();
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeSidebar, showSidebar]);

  const dispatch = useAppDispatch();
  const logout = () =>
    dispatch(logoutUserThunk())
      .unwrap()
      .then(() => dispatch(clearGetCareState()));

  return (
    <>
      {isMobile && (
        <div
          className={`app__sidebar-header d-md-none ${showSidebar ? 'is-active' : ''}`}
          ref={headerRef}
        >
          <Link
            className='bg-transparent border-0 p-0'
            to={DEFAULT_APP_LAYOUT_ROUTE}
            onClick={closeSidebar}
          >
            <img
              className={`app__sidebar-brand${sidebarMenuCollapsed ? '-collapsed' : ''}`}
              src={sidebarMenuCollapsed ? logoMark : logoImg}
              alt='sidebar-logo'
            />
          </Link>
          <button
            className='app__sidebar-btn'
            aria-label='Toggle sidebar menu'
            onClick={handleSidebarChange}
          >
            <img src={MenuIcon} alt='' />
          </button>
        </div>
      )}
      <nav
        ref={sidebarRef}
        className={`d-md-flex app__sidebar app__sidebar${sidebarMenuCollapsed ? '-collapsed' : ''} ${
          showSidebar ? 'is-sidebar-active' : ''
        }`}
      >
        <div
          role='button'
          tabIndex={0}
          onClick={() => dispatch(toggleMenuCollapsed())}
          onKeyDown={(e) => e.key === 'Enter' && dispatch(toggleMenuCollapsed())}
          className={`app__sidebar__collapsed-wrapper ${
            sidebarMenuCollapsed ? 'app__sidebar__collapsed-wrapper-active' : ''
          }`}
        >
          <img
            className='app__sidebar__collapsed-icon'
            src={leftRightArrowsImg}
            alt='sidebar-toggle'
          />
        </div>
        <div className='d-flex flex-column justify-content-between flex-grow-1'>
          <div>
            <div
              className={`app__sidebar-brandwrap ${sidebarMenuCollapsed ? 'app__sidebar-link' : ''}`}
            >
              <Link
                className='bg-transparent border-0 p-0'
                to={DEFAULT_APP_LAYOUT_ROUTE}
                onClick={closeSidebar}
              >
                <img
                  className={`app__sidebar-brand${sidebarMenuCollapsed ? '-collapsed' : ''}`}
                  src={logoMark}
                  alt='sidebar-logo'
                />
              </Link>
            </div>
            <SidebarMenu isMenuCollapsed={sidebarMenuCollapsed} closeSidebar={closeSidebar} />
          </div>
          {!sidebarMenuCollapsed && <SidebarCard />}
        </div>

        <div>
          <div className='app__sidebar-divider'></div>
          <SidebarProfile setShowLogoutModal={setShowConfirmMessageDialog} />
        </div>
        <CustomModal
          size={'sm'}
          show={showConfirmMessageDialog}
          title={t('Logout')}
          helpText={t('Are you sure you want logout?')}
          confirmText='Logout'
          cancelText='Cancel'
          handleConfirm={() => {
            setShowConfirmMessageDialog(false);
            logout();
          }}
          handleCancel={() => {
            setShowConfirmMessageDialog(false);
          }}
        />
      </nav>
    </>
  );
};

export default Sidebar;
